import {
    endOfDay,
    endOfMonth,
    format,
    isAfter,
    isBefore,
    isValid as isValidDate,
    startOfDay,
    startOfMonth,
    startOfYear,
    subDays,
    subMonths,
    subYears
} from 'date-fns';
import { TimePeriods, validationMessages } from '../../constants';
import { terms as sugAgentsTerms } from '../../agent-production/constants/suggestedAgents';
import { recurrences } from '../constants/date';

const getMinTimePeriodDate = () => {
    let dateMin = new Date();
    dateMin.setMonth(dateMin.getMonth() - 39);
    dateMin.setDate(0);
    return dateMin;
};

export const formatTransformedDate = (date, formatString) => {
    const transformedDate = new Date(date.toISOString().slice(0, -1));
    return format(transformedDate, formatString);
};

export const formatMediumSimpleDate = date => {
    return format(date, 'MMM dd, yyyy');
};

export const formatMediumDate = date => {
    return formatTransformedDate(date, 'MMM dd, yyyy');
};

export const formatShortDate = date => {
    return formatTransformedDate(date, 'MM/dd/yyyy');
};

const hasValidYear = date => {
    return date.getFullYear().toString().length === 4;
};

export const validateTimeFrame = (timeFrame, today = new Date()) => {
    let validationMessage = '';
    let isValid = true;
    let startDateInvalid = false;
    let endDateInvalid = false;
    const dateMin = getMinTimePeriodDate();
    if (!timeFrame.type) {
        isValid = false;
        validationMessage += validationMessages.missingTimePeriodType;
    }
    if (timeFrame.type === TimePeriods['Custom Date']) {
        let startDate, endDate;
        if (!timeFrame.startDate) {
            validationMessage += validationMessages.missingStartDate;
            isValid = false;
            startDateInvalid = true;
        } else {
            startDate = new Date(new Date(timeFrame.startDate).toISOString().slice(0, -1));
            if (!isValidDate(startDate) || !hasValidYear(startDate)) {
                validationMessage += validationMessages.invalidStartDateFormat;
                isValid = false;
                startDateInvalid = true;
            } else {
                if (isBefore(startDate, startOfDay(new Date(dateMin.toISOString()))) || isAfter(endOfDay(startDate), today)) {
                    validationMessage += validationMessages.invalidStartDate(formatMediumSimpleDate(dateMin), formatMediumSimpleDate(today));
                    isValid = false;
                    startDateInvalid = true;
                }
            }
        }

        if (!timeFrame.endDate) {
            validationMessage += validationMessages.missingEndDate;
            isValid = false;
            endDateInvalid = true;
        } else {
            endDate = new Date(new Date(timeFrame.endDate).toISOString().slice(0, -1));
            if (!isValidDate(endDate) || !hasValidYear(endDate)) {
                validationMessage += validationMessages.invalidEndDateFormat;
                isValid = false;
                endDateInvalid = true;
            } else {
                if (isBefore(endDate, startDate)) {
                    validationMessage += validationMessages.endDateTooEarly;
                    isValid = false;
                    startDateInvalid = true;
                }
                if (isAfter(endDate, endOfDay(today))) {
                    validationMessage += validationMessages.invalidEndDate(formatMediumSimpleDate(dateMin), formatMediumSimpleDate(today));
                    isValid = false;
                    endDateInvalid = true;
                }
            }
        }
        if (timeFrame.startDate === timeFrame.endDate) {
            validationMessage += validationMessages.sameStartAndEnd;
            isValid = false;
            startDateInvalid = true;
            endDateInvalid = true;
        }
    }
    return { isValid, validationMessage, startDateInvalid, endDateInvalid };
};

export const createLastXDaysInterval = (days, initialDate) => {
    initialDate.setHours(0, 0, 0, 0);

    const lastFrom = new Date(initialDate);
    lastFrom.setDate(lastFrom.getDate() - (days - 1));

    const lastYearFrom = new Date(lastFrom);
    lastYearFrom.setFullYear(lastYearFrom.getFullYear() - 1);

    const lastYearTo = new Date(initialDate);
    lastYearTo.setFullYear(lastYearTo.getFullYear() - 1);

    const previousFrom = new Date(lastFrom);
    previousFrom.setDate(previousFrom.getDate() - days);

    const previousTo = new Date(lastFrom);
    previousTo.setDate(previousTo.getDate() - 1);

    return {
        intervalType: `Last${days}Days`,
        lastIntervalText: sugAgentsTerms.getLastXDaysLabel(days),
        last: {
            from: lastFrom.toISOString().split("T")[0] + "T00:00:00",
            to: initialDate.toISOString().split("T")[0] + "T00:00:00"
        },
        lastYear: {
            from: lastYearFrom.toISOString().split("T")[0] + "T00:00:00",
            to: lastYearTo.toISOString().split("T")[0] + "T00:00:00"
        },
        previous: {
            from: previousFrom.toISOString().split("T")[0] + "T00:00:00",
            to: previousTo.toISOString().split("T")[0] + "T00:00:00"
        }
    };
};

export function getDateRangeFromRecurrence(recurrence) {
    const today = new Date();

    const recurrenceConfig = {
        [recurrences.lastThirtyDays]: {
            startDate: subDays(startOfDay(today), 30),
            endDate: endOfDay(today),
            previousStartDate: subDays(startOfDay(today), 60),
            previousEndDate: subDays(endOfDay(today), 30)
        },
        [recurrences.yearToDate]: {
            startDate: startOfYear(startOfDay(today)),
            endDate: endOfDay(today),
            previousStartDate: startOfYear(subYears(startOfDay(today), 1)),
            previousEndDate: subYears(endOfDay(today), 1)
        },
        [recurrences.lastTwelveMonths]: {
            startDate: subYears(startOfMonth(startOfDay(today)), 1),
            endDate: endOfMonth(subMonths(startOfMonth(endOfDay(today)), 1)),
            previousStartDate: subYears(startOfMonth(subYears(startOfDay(today), 1)), 1),
            previousEndDate: endOfMonth(subMonths(startOfMonth(subYears(endOfDay(today), 1)), 1))
        }
    };

    return recurrenceConfig[recurrence];
}
